import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { db, storage, auth } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Paperclip,
  Image as ImageIcon,
  Video as VideoIcon,
  Camera,
  Send,
  X,
  Check,
  CheckSquare,
  Trash2,
  FileText,
  ArrowLeft,
} from "lucide-react";

export default function ChatRoom({ otherUser, onClose, onBack }) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const isPromoUser = otherUser?.isPromo === true;
  const chatType = isPromoUser ? "chatsPromotional" : "chats";

  const chatId =
    currentUser.uid < otherUser.uid
      ? `${currentUser.uid}_${otherUser.uid}`
      : `${otherUser.uid}_${currentUser.uid}`;

  useEffect(() => {
    if (!chatId || !otherUser?.uid) {
      console.error("Invalid chatId or otherUser.uid:", { chatId, otherUser });
      setError("No se pudo cargar el chat. Inténtalo de nuevo.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const messagesRef = collection(db, chatType, chatId, "messages");
    const q = query(messagesRef, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const msgs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(msgs);
        setLoading(false);
        setError(null);

        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);

        const unreadMessages = msgs.filter(
          (msg) => msg.sender !== currentUser.uid && !msg.read
        );

        if (unreadMessages.length > 0) {
          try {
            await Promise.all(
              unreadMessages.map((msg) =>
                updateDoc(doc(db, chatType, chatId, "messages", msg.id), {
                  read: true,
                })
              )
            );
          } catch (error) {
            console.error("Error updating read messages:", error);
            setError("Error al actualizar el estado de los mensajes.");
          }
        }

        try {
          const chatRef = doc(db, chatType, chatId);
          await updateDoc(chatRef, {
            [`readStatus.${currentUser.uid}`]: true,
          });
        } catch (error) {
          console.error("Error updating readStatus:", error);
          setError("");
        }
      },
      (error) => {
        console.error("Error in onSnapshot:", error);
        setError("Error al cargar los mensajes. Inténtalo de nuevo.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [chatId, currentUser.uid, chatType, otherUser?.uid]);

  const uploadToFirebase = async (file) => {
    try {
      if (!file) throw new Error("No se proporcionó ningún archivo");

      // Crea un nombre único y carpeta (por ejemplo: "chats")
      const filePath = `chats/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);

      // Sube el archivo
      const snapshot = await uploadBytes(storageRef, file);

      // Obtén la URL pública del archivo subido
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error("Error subiendo archivo a Firebase Storage:", error);
      throw new Error("Falló la subida a Firebase Storage");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    try {
      const messagesRef = collection(db, chatType, chatId, "messages");
      const chatRef = doc(db, chatType, chatId);
      let uploadedAttachments = [];

      for (const file of attachments) {
        const url = await uploadToFirebase(file);
        let type = "file";
        if (file.type.startsWith("image")) type = "image";
        else if (file.type.startsWith("video")) type = "video";

        uploadedAttachments.push({ url, type, name: file.name });
      }

      await addDoc(messagesRef, {
        text: newMessage,
        sender: currentUser.uid,
        createdAt: serverTimestamp(),
        read: false,
        attachments: uploadedAttachments,
      });

      // 🔹 Obtener información del usuario receptor
      const otherUserDoc = await getDoc(doc(db, "users", otherUser.uid));
      const otherUserData = otherUserDoc.exists() ? otherUserDoc.data() : {};

      try {
        const isProm =
          otherUserData.role === "promocional" ||
          otherUserData.carrera === "Promocional";

        let toUid = otherUser.uid; // por defecto
        if (otherUser.isPromo && otherUser.ownerUid) {
          toUid = otherUser.ownerUid; // UID del dueño real
        }

        const idToken = await auth.currentUser.getIdToken();
        if (isProm) {
          // Solo notificación promocional
          await fetch(`${backendUrl}/api/send-prom-notification`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              to: toUid,
              from: currentUser.uid,
              message: newMessage,
              chatId,
            }),
          });
        } else {
          // Notificación normal
          await fetch(`${backendUrl}/api/send-notification`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              to: otherUser.uid,
              from: currentUser.uid,
              message: newMessage,
              chatId,
            }),
          });
        }
      } catch (err) {
        console.error("Error sending push notification:", err);
      }

      const chatDoc = await getDoc(chatRef);
      if (!chatDoc.exists()) {
        await setDoc(chatRef, {
          users: [currentUser.uid, otherUser.uid],
          lastMessage: newMessage,
          lastMessageTimestamp: serverTimestamp(),
          readStatus: {
            [currentUser.uid]: true,
            [otherUser.uid]: false,
          },
        });
      } else {
        await updateDoc(chatRef, {
          lastMessage: newMessage,
          lastMessageTimestamp: serverTimestamp(),
          [`readStatus.${currentUser.uid}`]: true,
          [`readStatus.${otherUser.uid}`]: false,
        });
      }

      setNewMessage("");
      setAttachments([]);
      setError(null);
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Error al enviar el mensaje. Inténtalo de nuevo.");
    }
  };

  const IMAGE_LIMIT_MB = 15;
  const VIDEO_LIMIT_MB = 30;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    let newAttachments = [...attachments];

    // Calcular tamaño actual total
    const currentImageSize = newAttachments
      .filter((f) => f.type?.startsWith("image"))
      .reduce((acc, f) => acc + f.size, 0);
    const currentVideoSize = newAttachments
      .filter((f) => f.type?.startsWith("video"))
      .reduce((acc, f) => acc + f.size, 0);

    let addedImageSize = 0;
    let addedVideoSize = 0;

    for (const file of files) {
      const isImage = file.type.startsWith("image");
      const isVideo = file.type.startsWith("video");
      const isFile =
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      const countImages = newAttachments.filter((f) => f.type?.startsWith("image")).length;
      const countVideos = newAttachments.filter((f) => f.type?.startsWith("video")).length;

      const swalWarn = (msg) => Swal.fire({ toast: true, position: "top-end", icon: "warning", title: msg, showConfirmButton: false, timer: 3500, timerProgressBar: true });

      if (isImage && countImages >= 5) {
        swalWarn("Solo puedes subir hasta 5 imágenes.");
        continue;
      }

      if (isVideo && countVideos >= 3) {
        swalWarn("Solo puedes subir hasta 3 videos.");
        continue;
      }

      if (!isImage && !isVideo && !isFile) {
        swalWarn("Tipo de archivo no permitido.");
        continue;
      }

      // Validar tamaño total
      if (isImage && (currentImageSize + addedImageSize + file.size) / (1024 * 1024) > IMAGE_LIMIT_MB) {
        swalWarn(`No puedes superar el tamaño total de imágenes (${IMAGE_LIMIT_MB} MB).`);
        continue;
      }

      if (isVideo && (currentVideoSize + addedVideoSize + file.size) / (1024 * 1024) > VIDEO_LIMIT_MB) {
        swalWarn(`No puedes superar el tamaño total de videos (${VIDEO_LIMIT_MB} MB).`);
        continue;
      }

      // Sumar tamaño agregado
      if (isImage) addedImageSize += file.size;
      if (isVideo) addedVideoSize += file.size;

      newAttachments.push(file);
    }

    setAttachments(newAttachments);
    e.target.value = null; // reset
  };


  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const isPending = currentUser?.status === "pending";
  const displayName = otherUser?.nombre || otherUser?.name || otherUser?.uid || "Usuario";
  const displayPhoto = otherUser?.photoURL || otherUser?.profilePhotoUrl || `https://ui-avatars.com/api/?name=${displayName}`;

  return (
<div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900 rounded shadow-md">
  <div className="flex items-center justify-between p-3 sm:p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">

        {/* Lado izquierdo: botón back + avatar + nombre */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">

          {/* Botón ← back (solo cuando viene de ChatRoomWrapper como ruta completa) */}
          {onBack && (
            <button
              onClick={onBack}
              className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-all text-gray-700 dark:text-gray-200"
              aria-label="Volver"
            >
              <ArrowLeft size={20} />
            </button>
          )}

          <img
            src={displayPhoto}
            alt={displayName}
            className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
          />
          <div className="flex flex-col min-w-0">
            <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
              {displayName}
            </p>
            {isPromoUser && (
              <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full w-fit">
                Cuenta Promocional
              </span>
            )}
          </div>
        </div>

        {/* Lado derecho: botón X (solo en modo modal/overlay) */}
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full hover:bg-red-100 dark:hover:bg-red-900 text-red-600 active:scale-95 transition-all"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 min-h-[200px]">
        {error && <p className="text-red-500 text-center">{error}</p>}
        {loading && <p className="text-center text-gray-500">Cargando mensajes...</p>}
        {!loading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-center text-gray-500">No hay mensajes aún</p>
          </div>
        )}
        {messages.map((msg) => {
          const isCurrentUser = msg.sender === currentUser.uid;
          return (
            <div
              key={msg.id}
              className={`max-w-xs px-4 py-2 rounded-lg break-words shadow-sm flex flex-col space-y-1 ${
                isCurrentUser
                  ? "bg-blue-600 text-white self-end ml-auto"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 self-start mr-auto"
              }`}
            >
              {msg.text && <span>{msg.text}</span>}
              {msg.attachments?.map((att, i) => (
                <div key={i} className="mt-1">
                  {att.type === "image" ? (
                    <img src={att.url} alt="Imagen adjunta" className="rounded max-h-48" />
                  ) : att.type === "video" ? (
                    <video controls className="rounded max-h-48">
                      <source src={att.url} type="video/mp4" />
                    </video>
                  ) : (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-200 underline"
                    >
                      <FileText size={16} />
                      {att.name || "Archivo"}
                    </a>
                  )}
                </div>
              ))}
              <span className="text-xs text-gray-400 self-start mt-1 select-none">
                {formatTime(msg.createdAt)}
              </span>
              {isCurrentUser && (
                <span className="flex items-center self-end">
                  {!msg.read ? (
                    <Check className="w-4 h-4 text-white opacity-70" title="Enviado" />
                  ) : (
                    <CheckSquare className="w-4 h-4 text-white opacity-90" title="Leído" />
                  )}
                </span>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {isPending ? (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-700 text-center">
          <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">
            ⏳ Cuenta pendiente de verificación — solo puedes leer mensajes.
          </p>
        </div>
      ) : null}
      <form
        onSubmit={handleSendMessage}
        className={`p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 ${isPending ? "hidden" : ""}`}
      >
        {attachments.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {attachments.map((file, i) => (
              <div key={i} className="relative group">
                {file.type?.startsWith("image") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="w-20 h-20 object-cover rounded"
                  />
                ) : file.type?.startsWith("video") ? (
                  <video src={URL.createObjectURL(file)} className="w-20 h-20 rounded" />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-600 text-sm flex items-center justify-center rounded text-center">
                    {file.name}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  className="absolute top-0 right-0 p-1 bg-red-600 text-white rounded-full"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-stretch gap-2 w-full">
          {/* Botón de adjuntar */}
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowOptions((prev) => !prev)}
              className="text-gray-600 dark:text-gray-300 hover:text-blue-500 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Paperclip className="w-5 h-5" />
            </button>

            {showOptions && (
              <div className="absolute z-10 bottom-12 left-0 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-md p-2 space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <ImageIcon className="w-4 h-4" /> <span>Imagen</span>
                  <input type="file" accept="image/*" onChange={handleFileChange} hidden />
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <VideoIcon className="w-4 h-4" /> <span>Video</span>
                  <input type="file" accept="video/*" onChange={handleFileChange} hidden />
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <Camera className="w-4 h-4" /> <span>Cámara</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} hidden />
                </label>
                <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <FileText className="w-4 h-4" /> <span>Archivo</span>
                  <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} hidden />
                </label>
              </div>
            )}
          </div>

           {/* Input y botón juntos, controlado */}
            <div className="flex flex-grow min-w-0 items-center gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="flex-grow min-w-0 px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />

              {/* Botón de enviar siempre a la derecha */}
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center flex-shrink-0"
                disabled={!newMessage.trim() && attachments.length === 0}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
      </form>
    </div>
  );
}